<template>
  <div class="leadList">
    <!-- Sticky Header -->
    <div class="lead-header">
      <v-container class="py-2">
        <v-row dense align="center">
          <v-col cols="12" sm="auto">
            <v-btn text dark small @click="showStudioPicker">
              <v-icon left>mdi-map-marker</v-icon>
              {{ studio && studio.name ? studio.name : "Studio wechseln" }}
            </v-btn>
          </v-col>
          <v-col cols="12" sm>
            <v-text-field
              v-model="search"
              clearable
              dark
              dense
              hide-details
              filled
              rounded
              prepend-inner-icon="mdi-magnify"
              placeholder="Suche Name, E-Mail, Telefon, Herkunft, Notiz…"
              :rules="rules" />
          </v-col>
          <v-col cols="auto">
            <v-btn icon dark @click="walkinDialog = true">
              <v-icon>mdi-account-plus</v-icon>
            </v-btn>
            <v-menu v-model="filterMenu" :close-on-content-click="false" offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon dark v-bind="attrs" v-on="on">
                  <v-icon :color="isFilterActive ? 'primary' : 'white'">mdi-filter-variant</v-icon>
                </v-btn>
              </template>
              <v-list dark dense class="lead-filter-menu">
                <v-subheader>Lead-Typen</v-subheader>
                <v-list-item>
                  <v-switch v-model="showWEGALeads" hide-details dark label="WEGA Leads" class="my-0 py-0" />
                </v-list-item>
                <v-list-item>
                  <v-switch v-model="showGlarnerMesseLeads" hide-details dark label="Glarner Messe Leads" class="my-0 py-0" />
                </v-list-item>
                <v-divider class="my-2" />
                <v-list-item class="d-block px-3 py-2">
                  <v-select
                    v-model="selectedGoals"
                    :items="goalOptions"
                    label="Trainingsziele"
                    item-text="text"
                    item-value="value"
                    multiple
                    small-chips
                    deletable-chips
                    clearable
                    outlined
                    dense
                    hide-details />
                </v-list-item>
                <v-list-item class="d-block px-3 py-2">
                  <v-select
                    v-model="sortBy"
                    :items="sortOptions"
                    label="Sortierung"
                    item-text="text"
                    item-value="value"
                    outlined
                    dense
                    hide-details />
                </v-list-item>
              </v-list>
            </v-menu>
            <v-btn v-if="userService.hasRight('stat_viewstudio')" icon dark @click="downloadExcel">
              <v-icon>mdi-file-excel-outline</v-icon>
            </v-btn>
          </v-col>
        </v-row>

        <!-- Status Chips, horizontal scrollbar -->
        <div class="status-chips mt-2">
          <v-chip-group dark v-model="selectedStatus" active-class="primary--text text--accent-4" mandatory show-arrows>
            <v-chip v-for="s in status" :key="s" :value="s" small>
              <span v-if="s.toString() !== 'Geschlossen'">{{ s }}</span>
              <span v-else>Nicht erreicht</span>
              <v-avatar v-if="statusCountsByStatusTitle(s)" right class="primary white--text" size="20">
                <small>{{ statusCountsByStatusTitle(s) }}</small>
              </v-avatar>
            </v-chip>
          </v-chip-group>
        </div>
      </v-container>
    </div>

    <v-container>
      <!-- Loading initial -->
      <div v-if="loading && leads.length === 0" class="d-flex justify-center align-center" style="min-height: 300px">
        <v-progress-circular indeterminate color="primary" size="48" />
      </div>

      <!-- Empty -->
      <v-alert v-else-if="!loading && leads.length === 0" type="info" border="left" outlined dark> Keine Leads gefunden </v-alert>

      <!-- Card Grid -->
      <div v-else class="lead-grid">
        <v-card v-for="lead of leads" :key="lead._id" class="lead-card" dark rounded="xl" elevation="4">
          <!-- Header -->
          <div class="lead-card__header">
            <v-avatar :color="avatarColor(lead)" size="44">
              <span class="white--text font-weight-bold">{{ initials(lead) }}</span>
            </v-avatar>
            <div class="lead-card__title">
              <div class="text-subtitle-1 font-weight-medium text-truncate d-flex justify-between align-center">
                {{ (lead.surname || "") + " " + (lead.name || "") || "Unbekannt" }}

                <v-btn icon x-small class="lead-row__copy ml-auto" @click="copyToClipboard(`${lead.surname || ''} ${lead.name}`, 'Name')">
                  <v-icon x-small>mdi-content-copy</v-icon>
                </v-btn>
              </div>
              <div class="lead-card__dates grey--text">
                <span>
                  <v-icon x-small>mdi-calendar-plus</v-icon>
                  Erstellt {{ lead.createdAt | moment("DD.MM.YYYY HH:mm") }}
                </span>
                <span v-if="isLeadUpdated(lead)">
                  <v-icon x-small>mdi-calendar-edit</v-icon>
                  Aktualisiert {{ lead.updatedAt | moment("DD.MM.YYYY HH:mm") }}
                </span>
              </div>
            </div>
          </div>

          <!-- Tags -->
          <div v-if="lead.info || lead.wheelPrize" class="lead-card__tags">
            <v-chip v-if="lead.info && lead.info.title" small color="primary" outlined>
              {{ checkTitle(lead.info.title) }}
            </v-chip>
            <v-chip v-if="lead.wheelPrize" small color="amber" outlined>
              <v-icon x-small left>mdi-gift-outline</v-icon>{{ lead.wheelPrize }}
            </v-chip>
          </div>

          <v-divider class="my-1" />

          <!-- Body -->
          <div class="lead-card__body">
            <div v-if="lead.email" class="lead-row">
              <v-icon small>mdi-email-outline</v-icon>
              <a :href="`mailto:${lead.email}`" class="text-truncate">{{ lead.email }}</a>
              <v-btn icon x-small class="lead-row__copy" @click.stop.prevent="copyToClipboard(lead.email, 'E-Mail')">
                <v-icon x-small>mdi-content-copy</v-icon>
              </v-btn>
            </div>
            <div v-if="lead.phone" class="lead-row">
              <v-icon small>mdi-phone-outline</v-icon>
              <a :href="`tel:${lead.phone}`">{{ lead.phone }}</a>
              <v-btn icon x-small class="lead-row__copy" @click.stop.prevent="copyToClipboard(lead.phone, 'Telefon')">
                <v-icon x-small>mdi-content-copy</v-icon>
              </v-btn>
            </div>
            <div v-if="lead.referralSource" class="lead-row lead-row--source">
              <v-icon small>mdi-bullhorn-outline</v-icon>
              <span><strong>Aufmerksam geworden:</strong> {{ sourceLabel(lead) }}</span>
            </div>
            <div v-if="lead.message" class="lead-row lead-row--msg">
              <v-icon small class="align-self-start mt-1">mdi-message-text-outline</v-icon>
              <span>{{ lead.message }}</span>
            </div>
            <div v-if="lead.referralMail || (lead.roodyUser && (lead.roodyUser.vorname || lead.roodyUser.nachname))" class="lead-row">
              <v-icon small>mdi-account-arrow-right-outline</v-icon>
              <span class="text-truncate">
                {{
                  lead.referralMail || ((lead.roodyUser && lead.roodyUser.vorname) || "") + " " + ((lead.roodyUser && lead.roodyUser.nachname) || "")
                }}
              </span>
            </div>
          </div>

          <div v-if="lead.goals && lead.goals.length" class="lead-card__goals">
            <div class="lead-card__goals-label">
              <v-icon small>mdi-target</v-icon>
              Trainingsziele
            </div>
            <div class="lead-card__goal-chips">
              <v-chip v-for="goal in lead.goals" :key="goal" small color="primary" outlined>
                {{ goalLabel(goal, lead.goalOther) }}
              </v-chip>
            </div>
          </div>

          <v-textarea
            v-model="lead.notes"
            @blur="saveLead(lead)"
            @focus="setUneditedText(lead.notes)"
            :loading="lead.loading"
            label="Anmerkungen"
            rows="2"
            auto-grow
            dense
            outlined
            hide-details
            class="mx-3 my-2 lead-card__notes" />

          <!-- Actions -->
          <div class="lead-card__actions">
            <v-select v-model="lead.status" :items="statusFiltered" dense hide-details outlined class="lead-card__select" />
            <v-select
              v-model="lead.studio"
              :items="studios"
              item-text="name"
              item-value="id"
              dense
              hide-details
              outlined
              class="lead-card__select mt-2" />
            <v-btn block color="primary" class="mt-2" @click="updateLead(lead)"> <v-icon left>mdi-content-save-outline</v-icon>Speichern </v-btn>
          </div>
        </v-card>
      </div>

      <!-- Infinite Scroll Sentinel -->
      <div v-if="hasMore && leads.length > 0" v-intersect="onBottomIntersect" class="d-flex justify-center py-8">
        <v-progress-circular v-if="loadingMore" indeterminate color="primary" />
      </div>
    </v-container>

    <!-- Walk-In Dialog -->
    <v-dialog max-width="500px" v-model="walkinDialog">
      <v-card dark class="px-3 pt-3 pb-5">
        <v-card-title>Interessent hinzufügen</v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="6"><v-text-field label="Vorname" outlined dense v-model="walkin.surname" hide-details /></v-col>
            <v-col cols="6"><v-text-field label="Name" outlined dense v-model="walkin.name" hide-details /></v-col>
            <v-col cols="12"><v-text-field label="E-Mail" outlined dense v-model="walkin.email" hide-details /></v-col>
            <v-col cols="12"><v-text-field label="Telefon" outlined dense v-model="walkin.phone" hide-details /></v-col>
            <v-col cols="12">
              <v-select outlined dense hide-details label="Typ" v-model="walkin.type" :items="walkinTypes" />
            </v-col>
            <v-col v-if="walkin.type === '66fe3b782ce4896b82c40312'" cols="12">
              <v-select
                outlined
                dense
                hide-details
                label="Preis"
                v-model="walkin.wheelPrize"
                :items="['+1 Monat', '+2 Monate', '11 Tage', 'Aktivierungs-Tarif geschenkt']" />
            </v-col>
            <v-col v-if="walkin.type === '66fe3b782ce4896b82c40312' || walkin.type === '69674aed4fbf1e45e3b62037'" cols="12">
              <v-select
                outlined
                dense
                hide-details
                label="Studio"
                v-model="walkin.studio"
                :items="studios.map((e) => ({ text: e.name, value: e.id }))" />
            </v-col>
            <v-col cols="12"><v-textarea label="Notiz" outlined dense v-model="walkin.note" rows="2" hide-details /></v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="walkinDialog = false">Abbrechen</v-btn>
          <v-btn color="primary" @click="addWalkin"><v-icon left>mdi-plus</v-icon>Hinzufügen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "../../../api"
import zipcelx from "zipcelx"
import userService from "@/services/userService"

export default {
  data() {
    return {
      userService,
      // UI
      filterMenu: false,
      walkinDialog: false,
      showWEGALeads: false,
      showGlarnerMesseLeads: false,
      selectedGoals: [],
      sortBy: "updatedAt",
      // Data
      leads: [],
      status: [],
      statusCounts: [],
      studios: [],
      studio: null,
      selectedStatus: "Wurde angeworben",
      // Pagination
      page: 0,
      hasMore: true,
      loading: true,
      loadingMore: false,
      error: false,
      // Search
      search: "",
      searchTimeout: null,
      // Edit tracking
      uneditedText: "",
      // Walkin
      walkin: {},
      walkinTypes: [
        { text: "Hyrox St.Gallen Interessent", value: "69674aed4fbf1e45e3b62037" },
        { text: "WEGA", value: "68d4eb3a65eef0a633ee084d" },
        { text: "Glarner Messe", value: "6902185897ad1a3851468b8a" },
        { text: "Glücksrad", value: "66fe3b782ce4896b82c40312" },
        { text: "Walk-In", value: "65000dc796e439bafefaf490" },
        { text: "Telefon", value: "6501b2707e1d78fc0506e23b" },
        { text: "E-Mail", value: "6501b2a27e1d78fc0506e29c" },
        { text: "Promotion / Messe", value: "65327e5379da10a6c15e8f79" },
      ],
      goalOptions: [
        { text: "Abnehmen", value: "weight_loss" },
        { text: "Muskelaufbau", value: "muscle_gain" },
        { text: "Straffen", value: "toning" },
        { text: "Ausdauer verbessern", value: "endurance" },
        { text: "Rücken kräftigen", value: "back_strength" },
        { text: "Schmerzen beseitigen", value: "pain_relief" },
        { text: "Bewegungsdrang ausleben", value: "movement" },
        { text: "Beweglichkeit verbessern", value: "mobility" },
        { text: "Koordination verbessern", value: "coordination" },
        { text: "Gesund bleiben", value: "health" },
        { text: "Haltung verbessern", value: "posture" },
        { text: "Aufbau nach Verletzung", value: "rehabilitation" },
        { text: "Bevorstehender Wettkampf", value: "competition" },
        { text: "Andere", value: "other" },
        { text: "Keine Angabe", value: "none" },
      ],
      sortOptions: [
        { text: "Zuletzt aktualisiert", value: "updatedAt" },
        { text: "Neu erstellt", value: "createdAt" },
      ],
      rules: [(v) => !v || !/\s\s/.test(v) || "Keine doppelten Leerzeichen"],
    }
  },
  async created() {
    let studio = await api.getUserStudio().catch(() => {
      window.app.$root.$children[0].checkStudio()
    })
    if (!studio) return
    this.studio = studio.studio

    try {
      let studios = await api.fetchStudios()
      const filterCodes = ["ot", "alibey"]
      this.studios = studios.filter((e) => !filterCodes.includes(e.code)).map((e) => ({ id: e._id, name: e.name }))
    } catch (e) {}

    this.init(true)
  },
  computed: {
    isFilterActive() {
      const hasGoalFilter = Array.isArray(this.selectedGoals) && this.selectedGoals.length > 0
      return this.showWEGALeads || this.showGlarnerMesseLeads || hasGoalFilter || this.sortBy !== "updatedAt"
    },
    statusFiltered() {
      return this.status
        .filter((e) => e.toString() !== "Lead Generate")
        .map((s) => ({
          text: s.toString() !== "Geschlossen" ? s : "Nicht erreicht (geschlossen)",
          value: s,
        }))
    },
    leadSettingFilter() {
      if (this.showWEGALeads) return "wega"
      if (this.showGlarnerMesseLeads) return "glarner"
      return "default"
    },
  },
  watch: {
    search() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => this.init(true), 400)
    },
    selectedStatus(newV, oldV) {
      if (newV !== oldV) this.init(true)
    },
    showWEGALeads(v) {
      if (v) this.showGlarnerMesseLeads = false
      this.init(true)
    },
    showGlarnerMesseLeads(v) {
      if (v) this.showWEGALeads = false
      this.init(true)
    },
    selectedGoals: {
      deep: true,
      handler() {
        this.init(true)
      },
    },
    sortBy() {
      this.init(true)
    },
  },
  methods: {
    async copyToClipboard(text, label = "Wert") {
      if (!text) return
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text)
        } else {
          // Fallback für ältere Browser / nicht-HTTPS
          const ta = document.createElement("textarea")
          ta.value = text
          ta.style.position = "fixed"
          ta.style.opacity = "0"
          document.body.appendChild(ta)
          ta.focus()
          ta.select()
          document.execCommand("copy")
          document.body.removeChild(ta)
        }
        this.$toast.success(`${label} kopiert`)
      } catch (e) {
        this.$toast.error("Kopieren fehlgeschlagen")
      }
    },
    statusCountsByStatusTitle(title) {
      const s = this.statusCounts.find((e) => e.status === title)
      return s?.count || ""
    },
    initials(lead) {
      const a = (lead.surname || "").charAt(0)
      const b = (lead.name || "").charAt(0)
      return (a + b).toUpperCase() || "?"
    },
    avatarColor(lead) {
      const colors = ["primary", "indigo", "teal", "deep-purple", "blue-grey", "cyan", "pink"]
      const id = (lead._id || "").toString()
      let h = 0
      for (let i = 0; i < id.length; i++) h = (h + id.charCodeAt(i)) % colors.length
      return colors[h]
    },
    checkTitle(title) {
      if (title.includes("Weiterempfehlung")) return "Weiterempfehlung"
      return title
    },
    goalLabel(goal, goalOther = "") {
      if (goal === "other" && goalOther) return `Andere: ${goalOther}`
      return this.goalOptions.find((option) => option.value === goal)?.text || goal
    },
    sourceLabel(lead) {
      if (lead.referralSource === "Andere" && lead.referralSourceOther) return `Andere: ${lead.referralSourceOther}`
      return lead.referralSource
    },
    isLeadUpdated(lead) {
      if (!lead.createdAt || !lead.updatedAt) return false
      return this.$moment(lead.updatedAt).diff(this.$moment(lead.createdAt), "seconds") > 1
    },
    setUneditedText(note) {
      this.uneditedText = note
    },
    showStudioPicker() {
      window.app.$root.$children[0].showStudioSelection = true
    },
    onBottomIntersect(entries) {
      if (entries[0].isIntersecting) this.loadMore()
    },
    async init(reset = false) {
      if (reset) {
        this.page = 0
        this.leads = []
        this.hasMore = true
        this.loading = true
      }
      try {
        const data = await api.fetchAllLeads({
          status: this.selectedStatus,
          page: this.page,
          limit: 30,
          search: this.search || "",
          leadSetting: this.leadSettingFilter,
          goals: this.selectedGoals || [],
          sortBy: this.sortBy,
        })
        if (data) {
          const incoming = data.leads || []
          this.leads = reset ? incoming : [...this.leads, ...incoming]
          if (data.staten) this.status = data.staten
          if (data.statusCounts) this.statusCounts = data.statusCounts
          this.hasMore = !!data.hasMore
        }
      } catch (e) {
        this.error = true
        this.$toast.error("Es ist ein Fehler aufgetreten")
      }
      this.loading = false
      this.$emit("finishedloading")
    },
    async loadMore() {
      if (this.loadingMore || !this.hasMore || this.loading) return
      this.loadingMore = true
      this.page += 1
      await this.init(false)
      this.loadingMore = false
    },
    async saveLead(lead) {
      if (lead.notes !== this.uneditedText) {
        this.$set(lead, "loading", true)
        const ok = await api.updateLead(lead).catch(() => this.$toast.error("Es gab einen Fehler"))
        if (ok) this.$toast.success("Erfolgreich aktualisiert")
        this.$set(lead, "loading", false)
      }
    },
    async updateLead(lead) {
      const ok = await api.updateLead(lead).catch(() => this.$toast.error("Es gab einen Fehler"))
      if (ok) {
        if (lead.status !== this.selectedStatus) {
          this.leads = this.leads.filter((l) => l._id !== lead._id)
        }
        console.log(lead.studio, this.studio._id)
        if (lead.studio !== this.studio._id) {
          this.leads = this.leads.filter((l) => l._id !== lead._id)
        }
        this.$toast.success("Erfolgreich aktualisiert")
      }
    },
    async addWalkin() {
      if (!this.walkin.type) return this.$toast.error("Bitte wähle einen Typen aus")
      if (this.walkin.type !== "66fe3b782ce4896b82c40312" && this.walkin.type !== "69674aed4fbf1e45e3b62037") {
        delete this.walkin.studio
        delete this.walkin.wheelPrize
      } else if (this.walkin.type === "66fe3b782ce4896b82c40312" && !this.walkin.wheelPrize) {
        return this.$toast.error("Bitte wähle einen Preis aus")
      }
      await api.createWalkin(this.walkin)
      this.walkin = {}
      this.walkinDialog = false
      this.init(true)
      this.$toast.success("Walk-In wurde hinzugefügt")
    },
    async downloadExcel() {
      try {
        this.$toast.info("Excel-Export wird vorbereitet...")

        // Alle Leads für den aktuellen Filter ohne Limit vom Server holen
        const response = await api.fetchLeadsForExport({
          status: this.selectedStatus,
          search: this.search || "",
          leadSetting: this.leadSettingFilter,
          goals: this.selectedGoals || [],
          sortBy: this.sortBy,
        })

        const exportLeads = response?.leads || []
        if (exportLeads.length === 0) {
          return this.$toast.error("Keine Leads zum Exportieren vorhanden")
        }

        const head = [
          "Erstellt",
          "Aktualisiert",
          "Formular-Titel",
          "Vorname",
          "Nachname",
          "E-Mail",
          "Telefon",
          "Weiterempfohlen von",
          "Aufmerksam geworden",
          "Trainingsziele",
          "Status",
        ]
        const data = [head.map((value) => ({ value, type: "string" }))]

        for (const lead of exportLeads) {
          data.push([
            { value: this.$moment(lead.createdAt).format("DD.MM.YYYY HH:mm"), type: "string" },
            { value: this.$moment(lead.updatedAt).format("DD.MM.YYYY HH:mm"), type: "string" },
            { value: lead.leadSetting?.title || "", type: "string" },
            { value: lead.name || "", type: "string" },
            { value: lead.surname || "", type: "string" },
            { value: lead.email || "", type: "string" },
            { value: lead.phone || "", type: "string" },
            {
              value: ((lead.roodyUser?.vorname || "") + " " + (lead.roodyUser?.nachname || "") + " " + (lead.referralMail || "")).trim(),
              type: "string",
            },
            { value: this.sourceLabel(lead) || "", type: "string" },
            { value: (lead.goals || []).map((goal) => this.goalLabel(goal, lead.goalOther)).join(", "), type: "string" },
            { value: lead.status || "", type: "string" },
          ])
        }

        zipcelx({ filename: `Leads_${this.selectedStatus}`, sheet: { data } })
        this.$toast.success("Excel erfolgreich heruntergeladen")
      } catch (e) {
        this.$toast.error("Fehler beim Excel-Export")
      }
    },
    downloadExcelOld() {
      const head = ["Datum", "Vorname", "Nachname", "E-Mail", "Telefon", "Weiterempfohlen von", "Status"]
      const data = [head.map((value) => ({ value, type: "string" }))]
      for (const lead of this.leads) {
        data.push([
          { value: this.$moment(lead.createdAt).format("DD.MM.YYYY HH:mm"), type: "string" },
          { value: lead.name || "", type: "string" },
          { value: lead.surname || "", type: "string" },
          { value: lead.email || "", type: "string" },
          { value: lead.phone || "", type: "string" },
          {
            value: ((lead.roodyUser?.vorname || "") + " " + (lead.roodyUser?.nachname || "") + " " + (lead.referralMail || "")).trim(),
            type: "string",
          },
          { value: lead.status || "", type: "string" },
        ])
      }
      zipcelx({ filename: "Leads", sheet: { data } })
    },
  },
}
</script>

<style lang="scss" scoped>
.leadList {
  min-height: 100vh;
}

.lead-header {
  position: sticky;
  top: 0;
  z-index: 5;
  background: rgba(20, 20, 25, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.lead-filter-menu {
  width: 340px;
  max-width: calc(100vw - 24px);
}

.status-chips {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar {
    height: 0;
  }
  ::v-deep .v-slide-group__content {
    flex-wrap: nowrap;
  }
  small {
    font-size: 0.75rem;
  }
}

.lead-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
}

.lead-card {
  display: flex;
  flex-direction: column;
  background: linear-gradient(160deg, #1f2027 0%, #15161b 100%) !important;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4) !important;
  }

  &__header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px 8px;
  }
  &__title {
    flex: 1;
    min-width: 0;
  }
  &__dates {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 12px;
    margin-top: 2px;
    font-size: 0.75rem;
    line-height: 1.4;
    span {
      display: inline-flex;
      align-items: center;
      gap: 3px;
    }
  }
  &__tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding: 0 16px 8px;
  }
  &__body {
    padding: 8px 16px;
  }
  &__goals {
    padding: 4px 16px 8px;
  }
  &__goals-label {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 7px;
    color: rgba(255, 255, 255, 0.72);
    font-size: 0.75rem;
    font-weight: 600;
  }
  &__goal-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    ::v-deep .v-chip {
      height: auto;
      min-height: 26px;
      white-space: normal;
    }
    ::v-deep .v-chip__content {
      padding-top: 4px;
      padding-bottom: 4px;
      white-space: normal;
      overflow-wrap: anywhere;
    }
  }
  &__notes {
    ::v-deep textarea {
      font-size: 0.875rem;
    }
  }
  &__actions {
    padding: 8px 16px 16px;
    margin-top: auto;
  }
  &__select {
    font-size: 0.875rem;
  }
}

.lead-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  margin: 6px 0;
  color: rgba(255, 255, 255, 0.85);
  a {
    flex: 1; // <-- hinzufügen, damit der Link den Platz füllt und der Copy-Button rechts sitzt

    color: inherit;
    text-decoration: none;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    &:hover {
      color: var(--v-primary-base);
    }
  }
  &__copy {
    opacity: 0.6;
    transition: opacity 0.15s ease;
    &:hover {
      opacity: 1;
    }
  }
  &--msg {
    align-items: flex-start;
    background: rgba(255, 255, 255, 0.04);
    padding: 8px 10px;
    border-radius: 8px;
    span {
      white-space: pre-wrap;
      word-break: break-word;
    }
  }
  &--source {
    align-items: flex-start;
    span {
      min-width: 0;
      overflow-wrap: anywhere;
    }
  }
}

@media (max-width: 600px) {
  .lead-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }
}
</style>
